import AsyncStorage from '@react-native-async-storage/async-storage';
import { Booking } from '../data/bookings';
import { SosAlert } from '../data/sos';
import { api } from './client';
import { ApiError } from './types';

/**
 * Raising an alarm, on a connection that may not be there.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT FOUR LINES LIKE EVERY OTHER SERVICE FILE
 * ---------------------------------------------------------------------------
 * Everything else in `services/` can fail honestly: the screen catches the
 * error, shows "couldn't reach Homingo", and offers Retry. That contract is
 * wrong here. Somebody who has pressed this button is not going to sit and
 * watch a spinner, and a network failure at that moment is exactly when the
 * alarm matters most - poor signal and being in trouble correlate.
 *
 * So `raise()` never rejects. It writes the alarm to disk FIRST, tries to send
 * it, and if the send fails the alarm stays on disk and is retried - on a
 * timer, on the next launch, and whenever the app comes back to the
 * foreground. The screen is told which of those states it is in and says so
 * plainly: "sent" is a different sentence from "we have it", and both are
 * different from "still trying".
 *
 * ---------------------------------------------------------------------------
 * THE IDEMPOTENCY KEY IS WHAT MAKES RETRYING SAFE
 * ---------------------------------------------------------------------------
 * Every press mints a `clientAlertId` that is stored beside the payload and
 * survives a restart. The server treats a repeat of that key as the same
 * incident and returns the original row, so the queue can be drained twice,
 * or by two overlapping timers, without raising two alarms for one press.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS STILL IS NOT
 * ---------------------------------------------------------------------------
 * "Backgrounded" here means the queue survives the app being closed and drains
 * when it is next opened or foregrounded. It does NOT mean the phone sends the
 * alarm while the app is dead - that needs a native headless task
 * (`react-native-background-actions`, WorkManager / BGTaskScheduler) and a
 * rebuild. An alarm raised with no signal on a phone that is then locked and
 * never reopened does not reach us, and nothing in this file pretends
 * otherwise.
 */

/** Where the queue lives. Namespaced like every other key in `storage.ts`. */
const QUEUE_KEY = 'homingo.sos.queue';

/**
 * How long between drain attempts while something is stuck, in ms.
 *
 * Short, and deliberately not backing off. Standard practice would widen the
 * gap after each failure to spare the server - but the thing being spared
 * here is an emergency, and eight seconds of extra delay to protect a backend
 * from one row is the wrong trade. The queue is at most a handful of items.
 */
const RETRY_MS = 8000;

/** What the device gathered, before it has been sent anywhere. */
export type SosDraft = {
  clientAlertId: string;
  raisedAt: string;
  bookingId?: string;
  /**
   * The trip this is about, when it is a trip rather than a visit.
   *
   * Not exclusive with `bookingId` - but for a customer it always is, because
   * a customer's ride is a standalone `customer_trip` with no booking behind
   * it. That is exactly why this field had to exist: without it an alarm
   * raised from inside a moving car reached ops carrying no vehicle, no plate,
   * no driver and no destination.
   */
  rideId?: string;
  lat?: number;
  lng?: number;
  locationAccuracyM?: number;
  locationAt?: string;
  addressText?: string;
  serviceTitle?: string;
  proName?: string;
};

/**
 * How the PLATFORM names an alert's state.
 *
 * Deliberately not `data/sos.ts`'s `SosStatus`, which is this app's own older
 * vocabulary - `active | false_alarm | closed` - and shares exactly one member
 * with the real one. Typing the server payload with it made `status === 'open'`
 * a compile error and, worse, made `closed` look like a legal thing to send:
 * the API validates against `SOS_OUTCOMES`, so it never was.
 */
export type ServerSosStatus =
  | 'open'
  | 'acknowledged'
  | 'resolved'
  | 'false_alarm';

/** One alarm as the server keeps it. */
export type ServerSosAlert = {
  id: string;
  status: ServerSosStatus;
  raisedAt: string;
  createdAt: string;
  bookingId?: string | null;
  rideId?: string | null;
  riderId?: string | null;
  lat?: number | null;
  lng?: number | null;
  addressText?: string | null;
  serviceTitle?: string | null;
  proName?: string | null;
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
  resolutionNotes?: string | null;
  /** Whether a human has picked it up. The one field the screen reads. */
  acknowledged: boolean;
};

/**
 * How far an alarm has got.
 *
 *   queued     on disk, not yet accepted - still trying
 *   sent       the server has it, nobody has picked it up yet
 *   acknowledged  a person has it
 *
 * The screen must distinguish `sent` from `acknowledged`. "We have sent this"
 * and "somebody is reading this" are different promises and conflating them is
 * the same mistake as collapsing a refund's initiated and settled states.
 */
export type SosDelivery = 'queued' | 'sent' | 'acknowledged';

export type SosState = {
  delivery: SosDelivery;
  draft: SosDraft;
  alert?: ServerSosAlert;
  /** How many sends have failed. Shown as reassurance, not as an error. */
  attempts: number;
};

// ---------------------------------------------------------------------------
// The queue on disk
// ---------------------------------------------------------------------------

type QueueEntry = {
  draft: SosDraft;
  attempts: number;
};

/**
 * Every read is defensive, matching `storage.ts`.
 *
 * Queue JSON is data written by an older build. Anything that fails to parse
 * becomes an empty queue rather than throwing - and it throws away at most a
 * stale alarm, whereas propagating the error would break the drain for every
 * future one.
 */
async function readQueue(): Promise<QueueEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(QUEUE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (entry): entry is QueueEntry =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as QueueEntry).draft?.clientAlertId === 'string' &&
        typeof (entry as QueueEntry).draft?.raisedAt === 'string',
    );
  } catch {
    return [];
  }
}

async function writeQueue(entries: QueueEntry[]): Promise<void> {
  try {
    await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(entries));
  } catch {
    /*
     * Disk full, or storage unavailable. Nothing useful to do - and crashing
     * the alarm path because the retry buffer could not be persisted would
     * trade a degraded feature for a broken one. The in-flight send below is
     * unaffected.
     */
  }
}

/** Idempotent by `clientAlertId`, so re-queuing a retry cannot duplicate it. */
async function enqueue(draft: SosDraft): Promise<void> {
  const queue = await readQueue();
  if (queue.some(entry => entry.draft.clientAlertId === draft.clientAlertId)) {
    return;
  }
  await writeQueue([...queue, { draft, attempts: 0 }]);
}

async function dequeue(clientAlertId: string): Promise<void> {
  const queue = await readQueue();
  await writeQueue(
    queue.filter(entry => entry.draft.clientAlertId !== clientAlertId),
  );
}

async function bumpAttempts(clientAlertId: string): Promise<number> {
  const queue = await readQueue();
  let attempts = 0;
  const next = queue.map(entry => {
    if (entry.draft.clientAlertId !== clientAlertId) {
      return entry;
    }
    attempts = entry.attempts + 1;
    return { ...entry, attempts };
  });
  await writeQueue(next);
  return attempts;
}

/** Whether anything is still waiting to go out. */
export const hasQueuedAlerts = async (): Promise<boolean> =>
  (await readQueue()).length > 0;

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

/**
 * The alarm routes, as the platform actually exposes them.
 *
 * These live under the actor prefix like every other customer route -
 * `RequireActorType('customer')` takes exactly one actor, so the Pro app has
 * its own mirror at `/pros/me/sos`. This file used to call a flat `/sos/alerts`
 * that has never existed on any deployment: every raise 404'd, the alarm was
 * queued to disk, the queue retried forever, and the screen sat on its red
 * spinner indefinitely. A person pressing that button was told help was on the
 * way and nothing had been sent.
 */
const SOS_BASE = '/customers/me/sos';

/**
 * The server's alert, in the shape this app reads.
 *
 * `acknowledged` is derived rather than sent: the API answers with `status`,
 * and the screen needs the boolean "has a human got this" - which is the one
 * distinction the copy on that screen turns on. Deriving it here keeps every
 * caller from re-deriving it slightly differently.
 */
const normalise = (raw: ServerSosAlert): ServerSosAlert => ({
  ...raw,
  acknowledged: raw.status === 'acknowledged',
});

const post = async (draft: SosDraft): Promise<ServerSosAlert> =>
  normalise(
    await api.post<ServerSosAlert>(SOS_BASE, draft),
  );

/**
 * Raise an alarm.
 *
 * Never rejects. Returns `queued` if the send did not land, and the queue
 * keeps trying in the background.
 *
 * The order matters: the draft is on disk BEFORE the request goes out, so an
 * app killed mid-request still has the alarm and sends it on next launch.
 */
export async function raise(draft: SosDraft): Promise<SosState> {
  await enqueue(draft);

  try {
    const alert = await post(draft);
    await dequeue(draft.clientAlertId);
    return {
      delivery: alert.acknowledged ? 'acknowledged' : 'sent',
      draft,
      alert,
      attempts: 0,
    };
  } catch (error) {
    /*
     * A 4xx other than auth means this alarm will never be accepted in its
     * current shape - a malformed date, say. Retrying it forever would block
     * the queue and pin a "still trying" message on a screen where it is a
     * lie. The row stays dropped and the screen is told it is queued, which is
     * the honest floor: we have it locally, we could not deliver it.
     *
     * 401 is exempt: the client refreshes and retries on its own, and a token
     * that expired mid-emergency must not discard the alarm.
     */
    const status = error instanceof ApiError ? error.status : 0;
    if (status >= 400 && status < 500 && status !== 401 && status !== 429) {
      await dequeue(draft.clientAlertId);
      return { delivery: 'queued', draft, attempts: 1 };
    }

    const attempts = await bumpAttempts(draft.clientAlertId);
    return { delivery: 'queued', draft, attempts };
  }
}

/**
 * Push whatever is still queued.
 *
 * Called on launch, on foreground, and on a timer while a screen is watching.
 * Safe to call concurrently: the server dedupes on `clientAlertId`, and the
 * in-process guard below keeps two timers from doing the same work twice.
 */
let draining = false;

export async function drainQueue(): Promise<ServerSosAlert[]> {
  if (draining) {
    return [];
  }
  draining = true;

  const sent: ServerSosAlert[] = [];
  try {
    const queue = await readQueue();
    for (const entry of queue) {
      try {
        const alert = await post(entry.draft);
        await dequeue(entry.draft.clientAlertId);
        sent.push(alert);
      } catch (error) {
        const status = error instanceof ApiError ? error.status : 0;
        if (status >= 400 && status < 500 && status !== 401 && status !== 429) {
          // Permanently unacceptable - see `raise`. Drop it rather than block.
          await dequeue(entry.draft.clientAlertId);
        } else {
          await bumpAttempts(entry.draft.clientAlertId);
        }
        /*
         * Keep going. One alarm failing must not strand the ones behind it -
         * they may be from a different incident entirely.
         */
      }
    }
  } finally {
    draining = false;
  }

  return sent;
}

/** The retry cadence, for a screen that wants to keep trying while it is open. */
export const SOS_RETRY_MS = RETRY_MS;

// ---------------------------------------------------------------------------
// Reading back
// ---------------------------------------------------------------------------

/**
 * The alarm still running, if any.
 *
 * What the app reopens onto: an incident must not vanish because the process
 * was killed. Returns null rather than throwing when it cannot be checked -
 * a failed poll is not evidence that nothing is active.
 */
export async function fetchActive(
  signal?: AbortSignal,
): Promise<ServerSosAlert | null> {
  try {
    /*
     * Derived from the list rather than fetched directly: the platform has no
     * "active alert" route, and adding one server-side to save a filter here
     * would be a route that exists for one caller. The list is newest-first
     * and capped at twenty, so the first non-terminal row IS the live one.
     */
    const alerts = await api.get<
      (ServerSosAlert)[] | null
    >(SOS_BASE, { signal });

    const live = (alerts ?? []).find(
      alert => alert.status === 'open' || alert.status === 'acknowledged',
    );
    return live ? normalise(live) : null;
  } catch {
    return null;
  }
}

/**
 * Stand it down.
 *
 * Rejects, unlike `raise` - this one runs when the customer is safe, so a
 * failure can be shown and retried like anything else. The screen still moves
 * on optimistically: telling somebody who is fine that they are still in an
 * emergency because a request failed is worse than a stale row ops can close.
 */
export const standDown = async (
  alertId: string,
  /*
   * `resolved`, not `closed`. The platform's own outcome vocabulary is
   * `resolved | false_alarm` (`SOS_OUTCOMES`), and sending anything else is
   * refused by the validator - this used to send `closed`, which no deployment
   * has ever accepted.
   */
  status: 'false_alarm' | 'resolved' = 'false_alarm',
): Promise<ServerSosAlert> =>
  normalise(
    await api.post<ServerSosAlert>(
      `${SOS_BASE}/${alertId}/stand-down`,
      { status },
    ),
  );

// ---------------------------------------------------------------------------
// Building a draft
// ---------------------------------------------------------------------------

/**
 * One press, as a payload.
 *
 * `clientAlertId` is minted here and nowhere else, so every retry of this
 * press carries the same key. Built from `Date.now()` and a random suffix
 * rather than a uuid library: the app has no uuid dependency, and the only
 * requirement is that two presses on one device never collide.
 */
export const draftFor = (
  booking: Booking | undefined,
  context: Partial<SosAlert>,
  fix?: { latitude: number; longitude: number; accuracy?: number; at?: string },
  /**
   * The live trip, when the alarm is raised from inside one.
   *
   * Passed separately from `booking` rather than folded into `context`
   * because it is a real foreign key the server checks ownership on, not a
   * display string. A customer ride has no booking, so on a ride alarm this
   * is the ONLY thing that tells ops which vehicle to look for.
   */
  ride?: { id: string },
): SosDraft => ({
  clientAlertId: `sos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  raisedAt: new Date().toISOString(),
  rideId: ride?.id,
  /*
   * The SERVER's booking id, not the app's display reference. `Booking.id` is
   * the row id everywhere the app talks to the API; `reference` is the
   * "#HOM12846" a human reads out and would not resolve here.
   */
  bookingId: booking?.id,
  lat: fix?.latitude,
  lng: fix?.longitude,
  locationAccuracyM: fix?.accuracy,
  locationAt: fix?.at,
  addressText: context.address,
  serviceTitle: context.service,
  proName: context.professional,
});
